import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, Sparkles, History as HistoryIcon, LogOut, Crown, Home } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";

const nav = [
    { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "side-dashboard" },
    { to: "/app/new", label: "New Repurpose", icon: Sparkles, testid: "side-new" },
    { to: "/app/history", label: "History", icon: HistoryIcon, testid: "side-history" },
];

export default function AppLayout() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate("/");
    };

    return (
        <div className="min-h-screen bg-[#050505] text-white flex">
            {/* Sidebar */}
            <aside className="hidden md:flex w-64 flex-col border-r border-white/10 bg-[#070707]">
                <div className="p-6 border-b border-white/10">
                    <Link to="/" data-testid="sidebar-logo-home" className="flex items-center gap-2 group" title="Back to homepage">
                        <div className="w-7 h-7 bg-white text-black flex items-center justify-center font-display font-black text-sm">R</div>
                        <span className="font-display font-bold tracking-tight group-hover:text-zinc-300 transition-colors">RepurposeAI</span>
                    </Link>
                </div>
                <nav className="flex-1 p-3 space-y-1">
                    {nav.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            data-testid={n.testid}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-colors ${
                                    isActive
                                        ? "bg-white text-black font-medium"
                                        : "text-zinc-400 hover:text-white hover:bg-white/5"
                                }`
                            }
                        >
                            <n.icon className="w-4 h-4" strokeWidth={1.5} />
                            {n.label}
                        </NavLink>
                    ))}
                </nav>
                <div className="p-3 border-t border-white/10 space-y-1">
                    <div className="px-3 py-2 text-xs text-zinc-500 flex items-center gap-2">
                        <Crown className="w-3 h-3" strokeWidth={1.5} />
                        <span className="uppercase tracking-widest font-mono-ui" data-testid="sidebar-plan">{user?.plan} plan</span>
                    </div>
                    <Link
                        to="/"
                        data-testid="sidebar-back-home"
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-md text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
                    >
                        <Home className="w-4 h-4" strokeWidth={1.5} />
                        Back to website
                    </Link>
                    <button
                        data-testid="sidebar-logout"
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-md text-zinc-400 hover:text-white hover:bg-white/5 transition-colors"
                    >
                        <LogOut className="w-4 h-4" strokeWidth={1.5} />
                        Logout
                    </button>
                </div>
            </aside>

            {/* Mobile header */}
            <div className="flex-1 flex flex-col min-w-0">
                <div className="md:hidden h-14 border-b border-white/10 bg-[#070707] flex items-center justify-between px-4">
                    <Link to="/" data-testid="mobile-logo-home" className="flex items-center gap-2" title="Back to homepage">
                        <div className="w-6 h-6 bg-white text-black flex items-center justify-center font-display font-black text-xs">R</div>
                        <span className="font-display font-bold text-sm">RepurposeAI</span>
                    </Link>
                    <div className="flex items-center gap-1">
                        <Link to="/" data-testid="mobile-back-home">
                            <Button size="sm" variant="ghost" className="text-zinc-400">
                                <Home className="w-4 h-4" />
                            </Button>
                        </Link>
                        <Button size="sm" variant="ghost" onClick={handleLogout} data-testid="mobile-logout" className="text-zinc-400">
                            <LogOut className="w-4 h-4" />
                        </Button>
                    </div>
                </div>
                <main className="flex-1 overflow-x-hidden">
                    <Outlet />
                </main>
            </div>
        </div>
    );
}
